import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Printer, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, inr, tokenHeaders } from "@/lib/api";
import { startPayment } from "@/lib/pay";
import { StatusBadge } from "@/components/site/Notices";

export default function OrderView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const headers = tokenHeaders("order", id);

  const load = useCallback(() => api.get(`/orders/${id}`, { headers: tokenHeaders("order", id) }).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/refresh-payment`, {}, { headers });
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const retry = async () => {
    setBusy(true);
    try {
      const res = await startPayment({ base: `/orders/${id}`, headers, description: `Order ${o.order_number}` });
      if (res.status !== "paid") toast.error(res.error || "Payment not completed");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="container-x py-24 text-center"><p data-testid="order-error">{err}</p><Link to="/my-orders" className="mt-4 inline-block underline">Find my orders</Link></div>;
  if (!o) return <div className="container-x py-24 text-center text-slate-500">Loading…</div>;
  const paid = o.payment_status === "paid";
  const result = params.get("result");

  return (
    <div className="container-x max-w-4xl py-12">
      <div className={`no-print mb-6 flex items-start gap-3 rounded-2xl p-5 ${paid ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-900"}`} data-testid={paid ? "order-success-banner" : "order-failure-banner"}>
        {paid ? <CheckCircle2 className="h-6 w-6 shrink-0" /> : <XCircle className="h-6 w-6 shrink-0" />}
        <div className="space-y-1">
          <p className="font-display text-lg font-bold">{paid ? "Payment verified — your order is confirmed" : "Payment not completed"}</p>
          <p className="text-sm">{paid ? "A receipt is shown below. You can print or save it." : result === "error" ? "We couldn't start the payment. Your order is saved; you can retry below." : "Your order is not paid yet. If money was debited, tap “Check payment status” — we confirm directly with Razorpay."}</p>
        </div>
      </div>
      {!paid && ["awaiting_payment", "payment_verification_pending"].includes(o.status) && (
        <div className="no-print mb-6 flex flex-wrap gap-3">
          <Button className="rounded-full bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={retry} data-testid="order-retry-payment">Retry payment</Button>
          <Button variant="outline" className="rounded-full" disabled={busy} onClick={refresh} data-testid="order-check-status"><RefreshCw className="h-4 w-4" /> Check payment status</Button>
        </div>
      )}
      <div className="print-area rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10" data-testid="order-receipt">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-6">
          <div>
            <p className="font-display text-xl font-extrabold">BMI SP <span className="text-amber-600">GROUP</span></p>
            <p className="text-xs text-slate-500">{paid ? "Order receipt / invoice" : "Order summary"}</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-mono-num font-bold" data-testid="order-number">{o.order_number}</p>
            <p className="text-slate-500">{fmtDate(o.created_at, true)}</p>
            <div className="mt-1"><StatusBadge status={o.status} testid="order-status" /></div>
          </div>
        </div>
        <div className="grid gap-6 py-6 text-sm sm:grid-cols-2">
          <div><p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Deliver to</p><p className="font-semibold">{o.customer.name}</p><p>{o.customer.address}{o.customer.landmark ? `, ${o.customer.landmark}` : ""}</p><p>{o.customer.city}, {o.customer.district}, {o.customer.state} – {o.customer.pincode}</p><p>{o.customer.mobile} · {o.customer.email}</p></div>
          <div><p className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Payment</p><p>Status: <strong data-testid="order-payment-status">{o.payment_status}</strong></p>{o.receipt_no && <p>Receipt: {o.receipt_no}</p>}{o.payment?.razorpay_payment_id && <p>Txn: <span className="font-mono-num">{o.payment.razorpay_payment_id}</span></p>}{o.paid_at && <p>Paid: {fmtDate(o.paid_at, true)}</p>}<p>Method: Online (Razorpay)</p></div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-y border-slate-100 text-left text-xs uppercase tracking-widest text-slate-500"><th className="py-2">Item</th><th>SKU</th><th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-right">Total</th></tr></thead>
          <tbody>{o.items.map((i) => <tr key={i.sku} className="border-b border-slate-50"><td className="py-2">{i.name} ({i.size})</td><td className="font-mono-num text-xs">{i.sku}</td><td className="text-right">{i.qty}</td><td className="text-right font-mono-num">{inr(i.unit_price)}</td><td className="text-right font-mono-num">{inr(i.line_total)}</td></tr>)}</tbody>
        </table>
        <div className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
          {o.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−{inr(o.discount)}</span></div>}
          <div className="flex justify-between"><span>Delivery</span><span>{inr(o.shipping)}</span></div>
          <div className="flex justify-between border-t pt-1 text-base font-bold"><span>Total</span><span className="font-mono-num" data-testid="order-total">{inr(o.total)}</span></div>
        </div>
      </div>
      <div className="no-print mt-6 flex flex-wrap gap-3">
        <Button variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="order-print-button"><Printer className="h-4 w-4" /> Print / Save as PDF</Button>
        <Link to="/products" className="rounded-full px-4 py-2 text-sm font-semibold underline">Continue shopping</Link>
      </div>
    </div>
  );
}
